import { useEffect, useState } from "react";
import { Clock, Lock } from "lucide-react";
import { canCreate, canEdit } from "@/lib/perms";
import { Comments } from "@/components/Comments";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Pencil, Trash2, Link2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useWorld } from "@/context/WorldContext";
import { OriginBadge } from "@/components/Origin";
import { api, backlinksFor } from "@/lib/api";
import { REL_TYPES } from "@/lib/constants";
import { CategoryBadge } from "@/components/CategoryBadge";
import { dedupeRels, shipLabel } from "@/lib/infobox";
import { PairingLine, ShipLabelBadge, ShipsPanel } from "@/components/ShipBits";
import { MembersPanel, RelatedSpeciesPanel, SpeciesMeta } from "@/components/SpeciesBits";
import { RichContent } from "@/components/RichContent";
import { Gallery } from "@/components/Gallery";
import { PageSections } from "@/components/BlockViews";
import { blockEmpty, quoteEmpty } from "@/lib/blocks";
import { collectionsOf } from "@/lib/collections";
import { PageHistory } from "@/components/PageHistory";
import { InfoboxView } from "@/components/InfoboxView";

const RelList = ({ page }) => {
  const { pages, relationships, basePath } = useWorld();
  const rels = dedupeRels(relationships.filter((r) => r.source_id === page.id || r.target_id === page.id));
  if (!rels.length) return null;
  return (
    <div className="rounded-xl border border-line bg-panel p-4" data-testid="page-relationships">
      <h3 className="font-display text-lg text-amber-100 mb-3">Bonds</h3>
      <div className="space-y-2">
        {rels.map((r) => {
          const other = pages.find((p) => p.id === (r.source_id === page.id ? r.target_id : r.source_id));
          if (!other) return null;
          return (
            <Link key={r.id} to={`${basePath}/p/${other.id}`} className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-300 transition-colors">
              <span className="w-2 h-2 rounded-full" style={{ background: REL_TYPES[r.type] }} />
              <span className="font-code text-[10px] uppercase tracking-wider text-slate-500 w-14 shrink-0">{r.type}</span>
              <span className="truncate">{other.title}{r.type === "ship" && <span className="text-pink-300/80 text-xs"> · {r.description}</span>}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default function PageView() {
  const { pageId } = useParams();
  const { pages, basePath, readOnly, reload, collections, world, profile, rootPath } = useWorld();
  const [mine, setMine] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (readOnly) return;
    api.get(`/pages/${pageId}`).then((r) => {
      setMine(r.data.my_pending);
      // Links from notifications point at canon; send profile entries to their own profile
      if ((r.data.profile_id || null) !== (profile?.id || null)) nav(`${rootPath}${r.data.profile_id ? `/fanon/${r.data.profile_id}` : ""}/p/${pageId}`, { replace: true });
    }).catch(() => {});
  }, [pageId, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  const page = pages.find((p) => p.id === pageId);
  if (!page) return <p className="p-12 text-slate-500" data-testid="page-not-found">This page has been lost to time.</p>;
  const backlinks = backlinksFor(pages, page);

  const remove = async () => {
    const { data } = await api.delete(`/pages/${page.id}`);
    await reload();
    toast.success(data.pending ? "Deletion sent for review" : "Page deleted");
    nav(data.pending ? `${basePath}/pending` : basePath);
  };

  return (
    <article className="px-6 md:px-10 py-10 grid xl:grid-cols-[minmax(0,1fr)_340px] gap-10" data-testid="page-view">
      <div className="min-w-0 rise">
        <div className="flex flex-wrap items-center gap-3">
          <CategoryBadge category={page.category} />
          {page.category === "Events" && page.event_date && (
            <span className="flex items-center gap-1 text-xs font-code text-rose-300" data-testid="page-event-date"><CalendarClock className="w-3.5 h-3.5" /> {page.event_date}</span>
          )}
          {!readOnly && (
            <div className="ml-auto flex gap-2">
              <PageHistory page={page} />
              {mine && <Link to={`${basePath}/pending?c=${mine.id}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-amber-200" data-testid="waiting-review-badge"><Clock className="w-3 h-3" /> {mine.kind === "suggestion" ? "Suggestion waiting" : "Waiting for review"}</Link>}
              {canEdit(world, page) ? (
                <Link to={`${basePath}/p/${page.id}/edit`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 transition-colors" data-testid="edit-page-btn">
                  <Pencil className="w-3 h-3" /> Edit
                </Link>
              ) : world.owner_pro && (
                <Link to={`${basePath}/p/${page.id}/edit?suggest=1`} title="You can suggest changes here" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 transition-colors" data-testid="suggest-page-btn">
                  <Lock className="w-3 h-3" /> Suggest changes
                </Link>
              )}
              {canCreate(world, page.category) && <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="inline-flex items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-rose-500/50 hover:text-rose-300 transition-colors" data-testid="delete-page-btn">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-panel border-line">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display text-2xl">Delete "{page.title}"?</AlertDialogTitle>
                    <AlertDialogDescription>Links to it will turn into unwritten pages.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-500" data-testid="delete-page-confirm">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>}
            </div>
          )}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 mt-4" data-testid="page-title">{page.title}</h1>
        <OriginBadge page={page} className="mt-3 text-[10px] px-2 py-0.5" />
        {page.category === "Ships" && <div className="flex flex-wrap items-center gap-3 mt-3"><PairingLine page={page} className="text-lg" /><ShipLabelBadge label={shipLabel(page)} /></div>}
        {page.category === "Species" && <SpeciesMeta page={page} className="mt-3" />}
        {collectionsOf(collections, page.category).some((c) => page.collection_ids?.includes(c.id)) && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="page-collections">
            {collectionsOf(collections, page.category).filter((c) => page.collection_ids?.includes(c.id)).map((c) => (
              <Link key={c.id} to={`${basePath}/c/${page.category.toLowerCase()}`} className="text-xs rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-200/90 px-2.5 py-0.5 hover:bg-amber-500/15 transition-colors" data-testid={`page-collection-${c.id}`}>{c.name}</Link>
            ))}
          </div>
        )}
        {page.summary && <p className="font-display italic text-xl text-slate-400 mt-3" data-testid="page-summary">{page.summary}</p>}
        <div className="h-px bg-gradient-to-r from-amber-500/40 via-line to-transparent my-8" />
        {page.content && <div className="mb-12"><RichContent html={page.content} /></div>}
        <PageSections page={page} />
        {!page.content && !(page.blocks || []).some((b) => !blockEmpty(b, page)) && quoteEmpty(page.pinned_quote) && <p className="text-slate-500 italic">This page awaits its story.</p>}
        <Gallery items={page.gallery} />
        {page.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10">
            {page.tags.map((t) => <span key={t} className="text-xs font-code text-slate-400 border border-line rounded-full px-2.5 py-0.5">#{t}</span>)}
          </div>
        )}
        {!readOnly && <Comments page={page} />}
      </div>
      <aside className="space-y-5 xl:sticky xl:top-20 xl:self-start xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto xl:pr-1" data-testid="page-aside">
        <InfoboxView page={page} />
        <RelList page={page} />
        {page.category === "Characters" && <ShipsPanel page={page} />}
        {page.category === "Species" && <><RelatedSpeciesPanel page={page} /><MembersPanel page={page} /></>}
        <div className="rounded-xl border border-line bg-panel p-4" data-testid="page-backlinks">
          <h3 className="font-display text-lg text-amber-100 mb-3 flex items-center gap-2"><Link2 className="w-4 h-4 text-amber-500" /> Referenced in</h3>
          {backlinks.length === 0 ? <p className="text-xs text-slate-500 italic">No pages link here yet.</p> : (
            <div className="space-y-1.5">
              {backlinks.map((b) => (
                <Link key={b.id} to={`${basePath}/p/${b.id}`} className="block text-sm text-slate-300 hover:text-amber-300 transition-colors truncate" data-testid={`backlink-${b.id}`}>{b.title}</Link>
              ))}
            </div>
          )}
        </div>
      </aside>
    </article>
  );
}
